/**
 * GÜNLÜK PUANTAJ — girişsiz bağlantı (/puantaj/:token).
 *
 * Departman sorumlusu telefonundan doldurur. Kimlik doğrulaması YOK: bağlantının
 * kendisi erişim anahtarı (bkz. routes/puantaj_linki.py). `lib/api.js`'teki JWT'li
 * istemci KASITLI olarak kullanılmıyor (PublicEmployeeForm.js'teki aynı not):
 * o ortak istemciye ileride "oturum gerekli" mantığı eklenirse bu sayfa kırılmasın.
 *
 * TASARIM (kullanıcı, 2026-09-18)
 * ===============================
 * Durumlar ONA YAKIN sayıda olduğu için düğme ızgarası yok: herkes "Çalıştı" ile
 * başlar, GÜN ekranında sağ/sol okla değiştirilir. Mesai kutusu kapalı gelir,
 * "+ Mesai" ile açılır.
 *
 * HAFTA ekranı da GİRİŞ YAPILAN bir ekran: hücreye dokununca alttan durum
 * listesi açılır, seçilenler biriktirilir ve altta tek "Kaydet" ile gönderilir.
 * Kaydetme aya göre gruplanıp her ayın kendi sürümüyle yazılıyor (hafta iki aya
 * yayılabiliyor) — bkz. `link_hafta_kaydet`.
 *
 * GİRİLMİŞ HÜCRE KİLİTLİDİR: geriye dönük giriş serbest olduğu için emniyet
 * buraya taşındı. Sorumlu eksikleri tamamlar, tamamlanmışı yalnızca yetkili
 * ekranlardan değiştirebilir.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  CheckCircle, AlertTriangle, Lock, Loader2, Plus, X,
  ChevronLeft, ChevronRight, CalendarDays, CalendarRange,
} from 'lucide-react';

const API = window.location.origin + '/api/erp/puantaj-linki';

// Durum renkleri (lib/puantaj.js ile uyumlu). Sıra sunucudan geliyor.
const RENK = {
  worked: 'bg-green-600 text-white border-green-600',
  weekly_leave: 'bg-blue-500 text-white border-blue-500',
  annual_leave: 'bg-amber-500 text-white border-amber-500',
  public_holiday: 'bg-cyan-500 text-white border-cyan-500',
  public_holiday_worked: 'bg-cyan-800 text-white border-cyan-800',
  unpaid_leave: 'bg-orange-700 text-white border-orange-700',
  sick_leave: 'bg-rose-500 text-white border-rose-500',
  excuse_leave: 'bg-teal-600 text-white border-teal-600',
  absent: 'bg-red-600 text-white border-red-600',
};
const NOTR = 'bg-[#faf7f2] text-[#3F3E39] border-[#e0d6c6]';

const GUN_ADI = ['Pazar', 'Pazartesi', 'Salı', 'Çarşamba', 'Perşembe', 'Cuma', 'Cumartesi'];
const GUN_KISA = ['Paz', 'Pzt', 'Sal', 'Çar', 'Per', 'Cum', 'Cmt'];
const gunuOku = (iso) => new Date(`${iso}T12:00:00`);
const gunAy = (iso) => { const d = gunuOku(iso); return `${d.getDate()}.${String(d.getMonth() + 1).padStart(2, '0')}`; };
const anahtar = (empId, tarih) => `${empId}|${tarih}`;
// Mesai iki yonlu: pozitif fazla mesai, negatif eksik calisma
const saatYaz = (s) => (s > 0 ? `+${s}` : `−${Math.abs(s)}`);

const tarihEtiketi = (iso, i) => `${i === 0 ? 'Bugün' : i === 1 ? 'Dün' : `${i} gün önce`} · ${gunAy(iso)} ${GUN_ADI[gunuOku(iso).getDay()]}`;

export default function PublicPuantajForm() {
  const { token } = useParams();
  const [gorunum, setGorunum] = useState('gun');     // 'gun' | 'hafta'
  const [tarih, setTarih] = useState(null);
  const [veri, setVeri] = useState(null);
  const [hafta, setHafta] = useState(null);
  const [satirlar, setSatirlar] = useState([]);
  const [mesaiAcik, setMesaiAcik] = useState({});    // {employee_id: true}
  const [bekleyen, setBekleyen] = useState({});      // hafta: {"emp|tarih": {durum, mesai_saat}}
  const [secilenHucre, setSecilenHucre] = useState(null);
  const [hata, setHata] = useState(null);            // 'gecersiz' | metin
  const [yukleniyor, setYukleniyor] = useState(true);
  const [kaydediyor, setKaydediyor] = useState(false);
  const [mesaj, setMesaj] = useState(null);          // {tur: 'ok'|'hata', metin}

  const yukle = useCallback(async (t) => {
    setYukleniyor(true); setMesaj(null);
    try {
      const r = await fetch(`${API}/${token}${t ? `?tarih=${t}` : ''}`);
      if (r.status === 404) { setHata('gecersiz'); return; }
      const j = await r.json();
      if (!r.ok) { setHata(j.detail || 'Sayfa açılamadı'); return; }
      setVeri(j); setTarih(j.tarih); setHata(null);
      setSatirlar(j.satirlar.map(s => ({ ...s, mesai_saat: s.mesai_saat ? String(s.mesai_saat) : '' })));
      // Dolu mesai kendiliğinden açık gelsin; boşlar "+ Mesai" ile açılır
      setMesaiAcik(Object.fromEntries(j.satirlar.filter(s => s.mesai_saat).map(s => [s.employee_id, true])));
    } catch {
      setHata('Bağlantı kurulamadı. İnternetinizi kontrol edip sayfayı yenileyin.');
    } finally { setYukleniyor(false); }
  }, [token]);

  const haftayiYukle = useCallback(async (t) => {
    setYukleniyor(true); setMesaj(null);
    try {
      const r = await fetch(`${API}/${token}/hafta${t ? `?tarih=${t}` : ''}`);
      if (r.status === 404) { setHata('gecersiz'); return; }
      const j = await r.json();
      if (!r.ok) { setHata(j.detail || 'Sayfa açılamadı'); return; }
      setHafta(j); setBekleyen({}); setSecilenHucre(null); setHata(null);
    } catch {
      setHata('Bağlantı kurulamadı. İnternetinizi kontrol edip sayfayı yenileyin.');
    } finally { setYukleniyor(false); }
  }, [token]);

  useEffect(() => { yukle(null); }, [yukle]);

  const haftayaGec = () => { setGorunum('hafta'); haftayiYukle(tarih); };
  const gunuAc = (t) => { setGorunum('gun'); yukle(t); };

  const guncelle = (id, alan, deger) =>
    setSatirlar(p => p.map(s => (s.employee_id === id ? { ...s, [alan]: deger } : s)));

  // Sağ/sol ok: durum listesinde dolaş. Uçlarda başa/sona sarıyor — telefonda
  // "bir tık geri" en sık düzeltme, duvara toslamak sinir bozucu.
  const durumKaydir = (s, yon) => {
    const liste = veri.durumlar.map(d => d.deger);
    const i = liste.indexOf(s.durum);
    const yeni = liste[((i < 0 ? 0 : i) + yon + liste.length) % liste.length];
    guncelle(s.employee_id, 'durum', yeni);
  };

  const kaydet = async () => {
    setKaydediyor(true); setMesaj(null);
    try {
      const r = await fetch(`${API}/${token}`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          tarih, surum: veri.surum,
          kayitlar: satirlar.filter(s => !s.kilitli).map(s => ({
            employee_id: s.employee_id, durum: s.durum,
            mesai_saat: s.mesai_saat === '' ? 0 : s.mesai_saat })),
        }),
      });
      const j = await r.json().catch(() => ({}));
      if (r.status === 404) { setHata('gecersiz'); return; }
      if (!r.ok) {
        setMesaj({ tur: 'hata', metin: j.detail || 'Kaydedilemedi' });
        if (r.status === 409) await yukle(tarih);   // güncel hali göster
        return;
      }
      await yukle(tarih);
      setMesaj({ tur: 'ok', metin: `Kaydedildi — ${j.kaydedilen} kişi${j.atlanan ? `, ${j.atlanan} kilitli satır değişmedi` : ''}.` });
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch {
      setMesaj({ tur: 'hata', metin: 'Bağlantı kurulamadı, tekrar deneyin.' });
    } finally { setKaydediyor(false); }
  };

  const haftayiKaydet = async () => {
    const degisiklikler = Object.entries(bekleyen).map(([k, v]) => {
      const [employee_id, t] = k.split('|');
      return { employee_id, tarih: t, durum: v.durum, mesai_saat: v.mesai_saat || 0 };
    });
    if (!degisiklikler.length) return;
    setKaydediyor(true); setMesaj(null);
    try {
      const r = await fetch(`${API}/${token}/hafta`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ surumler: hafta.surumler || {}, degisiklikler }),
      });
      const j = await r.json().catch(() => ({}));
      if (r.status === 404) { setHata('gecersiz'); return; }
      if (!r.ok) {
        setMesaj({ tur: 'hata', metin: j.detail || 'Kaydedilemedi' });
        if (r.status === 409) await haftayiYukle(hafta.gunler?.[0]?.tarih);
        return;
      }
      const tahakkuklu = (j.aylar || []).filter(a => a.durum === 'tahakkuklu').map(a => a.ay);
      await haftayiYukle(hafta.gunler?.[0]?.tarih);
      setMesaj({
        tur: 'ok',
        metin: `Kaydedildi — ${j.kaydedilen} giriş`
          + (j.atlanan ? `, ${j.atlanan} kilitli hücre atlandı` : '')
          + (tahakkuklu.length ? `. ${tahakkuklu.join(', ')} tahakkuk edilmiş, değiştirilemedi.` : '.'),
      });
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch {
      setMesaj({ tur: 'hata', metin: 'Bağlantı kurulamadı, tekrar deneyin.' });
    } finally { setKaydediyor(false); }
  };

  if (hata === 'gecersiz') {
    return (
      <Kabuk>
        <div className="text-center py-16 px-6">
          <AlertTriangle className="mx-auto text-[#a34b3f] mb-3" size={40} />
          <h1 className="text-lg font-bold text-[#1a1916]">Bağlantı geçersiz</h1>
          <p className="text-sm text-[#5c554b] mt-2">
            Bu bağlantı iptal edilmiş ya da artık kullanılmıyor. Güncel bağlantı için muhasebe ile görüşün.
          </p>
        </div>
      </Kabuk>
    );
  }
  if (hata) {
    return <Kabuk><p className="text-center py-16 px-6 text-sm text-[#a34b3f]">{hata}</p></Kabuk>;
  }
  if (!veri) {
    return <Kabuk><div className="flex justify-center py-20"><Loader2 className="animate-spin text-[#8a7e70]" /></div></Kabuk>;
  }

  const kilitli = Boolean(veri.kilit);
  const etiket = (deger) => veri.durumlar.find(d => d.deger === deger)?.etiket || deger;
  const kod = (deger) => veri.durumlar.find(d => d.deger === deger)?.kod || '?';
  const bekleyenSayisi = Object.keys(bekleyen).length;

  return (
    <Kabuk>
      <header className="px-4 pt-5 pb-3 bg-[#faf7f2] border-b border-[#d0c4b0] sticky top-0 z-10">
        <p className="text-xs text-[#8a7e70]">{veri.sirket_adi}</p>
        <div className="flex items-center justify-between gap-2">
          <h1 className="text-xl font-bold text-[#1a1916] leading-tight">
            {gorunum === 'gun' ? 'Günlük Puantaj' : 'Haftalık Puantaj'}
          </h1>
          <div className="flex rounded-lg border border-[#d0c4b0] overflow-hidden shrink-0">
            <button type="button" onClick={() => gunuAc(tarih)} data-testid="gorunum-gun"
              className={`px-2.5 py-1.5 text-xs flex items-center gap-1 ${gorunum === 'gun' ? 'bg-[#2e2d29] text-[#f5f0e8]' : 'bg-white text-[#3F3E39]'}`}>
              <CalendarDays size={13} /> Gün
            </button>
            <button type="button" onClick={haftayaGec} data-testid="gorunum-hafta"
              className={`px-2.5 py-1.5 text-xs flex items-center gap-1 border-l border-[#d0c4b0] ${gorunum === 'hafta' ? 'bg-[#2e2d29] text-[#f5f0e8]' : 'bg-white text-[#3F3E39]'}`}>
              <CalendarRange size={13} /> Hafta
            </button>
          </div>
        </div>
        <p className="text-sm text-[#3F3E39]">{veri.sorumlu_adi}</p>
        {gorunum === 'gun' && (
          <>
            <div className="mt-3 flex items-center gap-2">
              {/* Geçmişe serbest giriş: tarih seçici. Kısayollar yalnızca kolaylık. */}
              <input type="date" value={tarih || ''} max={veri.en_gec_tarih}
                onChange={(e) => e.target.value && yukle(e.target.value)} disabled={yukleniyor}
                className="h-10 rounded-lg border border-[#d0c4b0] bg-white px-2 text-sm"
                data-testid="puantaj-tarih-secici" />
              <span className="text-xs text-[#8a7e70]">{GUN_ADI[gunuOku(tarih || veri.tarih).getDay()]}</span>
            </div>
            <div className="flex gap-2 mt-2 overflow-x-auto -mx-4 px-4 pb-1">
              {(veri.hizli_tarihler || []).map((t, i) => (
                <button key={t} type="button" onClick={() => yukle(t)} disabled={yukleniyor}
                  className={`shrink-0 rounded-full px-3 py-1.5 text-xs border transition-colors
                    ${t === tarih ? 'bg-[#2e2d29] text-[#f5f0e8] border-[#2e2d29]' : 'bg-white text-[#3F3E39] border-[#d0c4b0]'}`}
                  data-testid={`puantaj-tarih-${i}`}>
                  {tarihEtiketi(t, i)}
                </button>
              ))}
            </div>
          </>
        )}
      </header>

      {mesaj && (
        <div className="px-3 pt-3">
          <div className={`flex gap-2 items-start rounded-lg px-3 py-2.5 text-sm
            ${mesaj.tur === 'ok' ? 'bg-green-50 text-green-800 border border-green-200' : 'bg-red-50 text-red-800 border border-red-200'}`}
            data-testid="puantaj-mesaj">
            {mesaj.tur === 'ok' ? <CheckCircle size={18} className="shrink-0" /> : <AlertTriangle size={18} className="shrink-0" />}
            <span>{mesaj.metin}</span>
          </div>
        </div>
      )}

      {gorunum === 'hafta' ? (
        <HaftaGorunumu hafta={hafta} yukleniyor={yukleniyor} kod={kod} etiket={etiket}
          durumlar={veri.durumlar} haftayiYukle={haftayiYukle}
          bekleyen={bekleyen} setBekleyen={setBekleyen}
          secilenHucre={secilenHucre} setSecilenHucre={setSecilenHucre}
          bekleyenSayisi={bekleyenSayisi} kaydediyor={kaydediyor} haftayiKaydet={haftayiKaydet} />
      ) : (
        <>
          <main className="px-3 py-3 pb-28 space-y-2">
            {kilitli && (
              <div className="flex gap-2 items-start rounded-lg px-3 py-2.5 text-sm bg-[#2e6fa0]/10 text-[#1f4f75] border border-[#2e6fa0]/30">
                <Lock size={18} className="shrink-0" /><span>{veri.kilit}</span>
              </div>
            )}
            {!kilitli && veri.tamamlandi && !mesaj && (
              <div className="flex gap-2 items-center rounded-lg px-3 py-2 text-sm bg-green-50 text-green-800 border border-green-200">
                <CheckCircle size={16} /> Bu günün puantajı girilmiş.
              </div>
            )}

            {satirlar.map(s => (
              <div key={s.employee_id}
                className={`rounded-xl border bg-white p-3 ${s.kilitli ? 'border-[#d0c4b0] opacity-80' : 'border-[#d0c4b0]'}`}
                data-testid={`puantaj-satir-${s.employee_id}`}>
                <div className="flex items-baseline justify-between gap-2">
                  <p className="font-semibold text-[#1a1916]">{s.ad}</p>
                  {s.pozisyon && <p className="text-xs text-[#8a7e70] truncate">{s.pozisyon}</p>}
                </div>
                {s.kilitli ? (
                  <div className="mt-2 flex items-center gap-2 flex-wrap">
                    <span className={`rounded-md border px-2 py-1 text-xs font-semibold ${RENK[s.durum] || NOTR}`}>
                      {etiket(s.durum)}{s.mesai_saat ? ` ${saatYaz(s.mesai_saat)}` : ''}
                    </span>
                    <span className="text-xs text-[#2e6fa0] flex items-center gap-1"><Lock size={12} /> {s.kilit_nedeni}</span>
                  </div>
                ) : (
                  <>
                    <div className="mt-2 flex items-stretch gap-1.5">
                      <button type="button" disabled={kilitli} onClick={() => durumKaydir(s, -1)}
                        aria-label="Önceki durum" data-testid={`puantaj-geri-${s.employee_id}`}
                        className="w-12 shrink-0 rounded-lg border border-[#d0c4b0] bg-[#faf7f2] text-[#3F3E39] flex items-center justify-center active:bg-[#e8dfd0] disabled:opacity-60">
                        <ChevronLeft size={22} />
                      </button>
                      <div className={`flex-1 rounded-lg border px-2 py-3 text-center text-[15px] font-semibold leading-tight ${RENK[s.durum] || NOTR}`}
                        data-testid={`puantaj-durum-${s.employee_id}`}>
                        {etiket(s.durum)}
                      </div>
                      <button type="button" disabled={kilitli} onClick={() => durumKaydir(s, 1)}
                        aria-label="Sonraki durum" data-testid={`puantaj-ileri-${s.employee_id}`}
                        className="w-12 shrink-0 rounded-lg border border-[#d0c4b0] bg-[#faf7f2] text-[#3F3E39] flex items-center justify-center active:bg-[#e8dfd0] disabled:opacity-60">
                        <ChevronRight size={22} />
                      </button>
                    </div>
                    {mesaiAcik[s.employee_id] ? (
                      <div className="mt-2 flex items-center gap-2 text-sm text-[#3F3E39]">
                        <span className="text-[#8a7e70]">Mesai (eksik için −)</span>
                        <input type="number" inputMode="decimal" step="any" min="-16" max="16" disabled={kilitli}
                          value={s.mesai_saat} placeholder="0" autoFocus
                          onChange={(e) => guncelle(s.employee_id, 'mesai_saat', e.target.value)}
                          className="w-20 h-10 rounded-lg border border-[#d0c4b0] px-2 text-base text-right"
                          data-testid={`puantaj-mesai-${s.employee_id}`} />
                        saat
                        <button type="button" aria-label="Mesaiyi kaldır"
                          onClick={() => { guncelle(s.employee_id, 'mesai_saat', ''); setMesaiAcik(p => ({ ...p, [s.employee_id]: false })); }}
                          className="ml-auto text-[#8a7e70] p-2"><X size={16} /></button>
                      </div>
                    ) : (
                      <button type="button" disabled={kilitli}
                        onClick={() => setMesaiAcik(p => ({ ...p, [s.employee_id]: true }))}
                        className="mt-2 inline-flex items-center gap-1 text-xs text-[#3d6e4e] px-1 py-1.5"
                        data-testid={`puantaj-mesai-ekle-${s.employee_id}`}>
                        <Plus size={14} /> Mesai
                      </button>
                    )}
                  </>
                )}
              </div>
            ))}
            {satirlar.length === 0 && (
              <p className="text-center text-sm text-[#8a7e70] py-10">Bu gün için listede personel yok.</p>
            )}
          </main>

          {!kilitli && satirlar.some(s => !s.kilitli) && (
            <div className="fixed bottom-0 inset-x-0 p-3 bg-[#faf7f2]/95 border-t border-[#d0c4b0] backdrop-blur">
              <button type="button" onClick={kaydet} disabled={kaydediyor || yukleniyor}
                className="w-full h-12 rounded-xl bg-[#2e2d29] text-[#f5f0e8] text-base font-semibold disabled:opacity-60 flex items-center justify-center gap-2"
                data-testid="puantaj-kaydet">
                {kaydediyor && <Loader2 size={18} className="animate-spin" />}
                {kaydediyor ? 'Kaydediliyor…' : 'Kaydet'}
              </button>
            </div>
          )}
        </>
      )}
    </Kabuk>
  );
}

/** Haftanın tamamı: satır personel, sütun gün. Hücreye dokun → durum seç → altta kaydet. */
function HaftaGorunumu({ hafta, yukleniyor, kod, etiket, durumlar, haftayiYukle,
  bekleyen, setBekleyen, secilenHucre, setSecilenHucre, bekleyenSayisi, kaydediyor, haftayiKaydet }) {
  // Tablo telefonda yatay kayıyor ve EN SAĞDAKİ gün en yenisi: açılışta oraya
  // kaydırmazsak sorumlu, en çok bakacağı sütunu görmüyor (2026-09-18 ölçüldü).
  const kaydirma = useRef(null);
  useEffect(() => {
    const el = kaydirma.current;
    if (el) el.scrollLeft = el.scrollWidth;
  }, [hafta]);

  const gunler = useMemo(() => hafta?.gunler || [], [hafta]);

  if (yukleniyor || !hafta) {
    return <div className="flex justify-center py-20"><Loader2 className="animate-spin text-[#8a7e70]" /></div>;
  }

  const hucreDegeri = (s, g) => {
    const b = bekleyen[anahtar(s.employee_id, g)];
    const h = s.gunler[g] || {};
    return { ...h, ...(b ? { durum: b.durum, mesai_saat: b.mesai_saat, bekliyor: true } : {}) };
  };

  const secim = secilenHucre && {
    ...secilenHucre,
    mevcut: bekleyen[anahtar(secilenHucre.employee_id, secilenHucre.tarih)]
      || (hafta.satirlar.find(s => s.employee_id === secilenHucre.employee_id)?.gunler?.[secilenHucre.tarih]),
  };

  const secimYaz = (durum, mesai) => {
    setBekleyen(p => ({ ...p, [anahtar(secilenHucre.employee_id, secilenHucre.tarih)]: { durum, mesai_saat: mesai || 0 } }));
    setSecilenHucre(null);
  };

  return (
    <main className="px-2 py-3 pb-28">
      <div className="flex items-center justify-between gap-2 mb-2 px-1">
        <button type="button" onClick={() => haftayiYukle(hafta.onceki_hafta)}
          className="flex items-center gap-1 text-sm text-[#3F3E39] px-2 py-1.5 rounded-lg border border-[#d0c4b0] bg-white"
          data-testid="hafta-onceki">
          <ChevronLeft size={16} /> Önceki
        </button>
        <span className="text-xs text-[#8a7e70]">
          {gunler.length ? `${gunAy(gunler[0].tarih)} – ${gunAy(gunler[gunler.length - 1].tarih)}` : ''}
        </span>
        <button type="button" onClick={() => haftayiYukle(hafta.sonraki_hafta)} disabled={!hafta.sonraki_hafta}
          className="flex items-center gap-1 text-sm text-[#3F3E39] px-2 py-1.5 rounded-lg border border-[#d0c4b0] bg-white disabled:opacity-40"
          data-testid="hafta-sonraki">
          Sonraki <ChevronRight size={16} />
        </button>
      </div>

      <div ref={kaydirma} className="overflow-x-auto rounded-xl border border-[#d0c4b0] bg-white">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-[#e8dfd0]">
              <th className="text-left font-bold text-xs uppercase text-[#1a1916] px-2 py-2 sticky left-0 bg-[#e8dfd0] border-r border-[#d0c4b0]">Personel</th>
              {gunler.map(g => (
                <th key={g.tarih} className="px-1 py-2 text-[11px] font-bold text-[#1a1916] whitespace-nowrap">
                  {GUN_KISA[gunuOku(g.tarih).getDay()]}<br />
                  <span className="font-normal text-[#8a7e70]">{gunAy(g.tarih)}</span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {hafta.satirlar.map(s => (
              <tr key={s.employee_id} className="border-t border-[#e0d6c6]" data-testid={`hafta-satir-${s.employee_id}`}>
                <td className="px-2 py-1.5 sticky left-0 bg-white border-r border-[#e0d6c6]">
                  {/* Ad sütunu dar tutuluyor: gün sütunlarına yer kalsın */}
                  <span className="block text-[13px] font-medium text-[#1a1916] truncate max-w-[104px]"
                    title={s.ad}>{s.ad}</span>
                </td>
                {gunler.map(g => {
                  const h = hucreDegeri(s, g.tarih);
                  if (!h.kadroda) return <td key={g.tarih} className="text-center text-[#c9bfae]">—</td>;
                  const yazilabilir = g.duzenlenebilir && !h.kilitli;
                  return (
                    <td key={g.tarih} className="px-1 py-1 text-center">
                      <button type="button" disabled={!yazilabilir}
                        onClick={() => setSecilenHucre({ employee_id: s.employee_id, ad: s.ad, tarih: g.tarih })}
                        title={`${etiket(h.durum)}${h.kilitli ? ` · ${h.kilit_nedeni}` : ''}`}
                        data-testid={`hafta-hucre-${s.employee_id}-${g.tarih}`}
                        className={`w-full min-w-[34px] rounded-md border px-1 py-1.5 text-[11px] font-bold
                          ${h.girilmis || h.bekliyor ? (RENK[h.durum] || NOTR) : 'bg-white text-[#c0b6a5] border-[#e0d6c6]'}
                          ${h.bekliyor ? 'ring-2 ring-offset-1 ring-[#2e2d29]' : ''}
                          ${yazilabilir ? '' : 'opacity-70'}`}>
                        {h.kilitli && !h.bekliyor ? '🔒' : kod(h.durum)}
                        {h.mesai_saat ? <span className="block font-normal">{saatYaz(h.mesai_saat)}</span> : null}
                      </button>
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="text-[11px] text-[#8a7e70] px-2 pt-2">
        Soluk hücreler henüz girilmemiş (varsayılan "Çalıştı" sayılır). Girmek için hücreye dokunun.
        🔒 olanlar girilmiş ya da onaylı izin; değişiklik için puantaj yetkilisine başvurun.
        Geçmiş haftalara "Önceki" ile gidebilirsiniz.
      </p>
      {hafta.satirlar.length === 0 && (
        <p className="text-center text-sm text-[#8a7e70] py-10">Bu hafta için listede personel yok.</p>
      )}

      {secim && (
        <DurumSecici secim={secim} durumlar={durumlar} kapat={() => setSecilenHucre(null)}
          yaz={secimYaz}
          sil={() => {
            setBekleyen(p => { const y = { ...p }; delete y[anahtar(secim.employee_id, secim.tarih)]; return y; });
            setSecilenHucre(null);
          }}
          bekliyorMu={Boolean(bekleyen[anahtar(secim.employee_id, secim.tarih)])} />
      )}

      {bekleyenSayisi > 0 && (
        <div className="fixed bottom-0 inset-x-0 p-3 bg-[#faf7f2]/95 border-t border-[#d0c4b0] backdrop-blur">
          <button type="button" onClick={haftayiKaydet} disabled={kaydediyor}
            className="w-full h-12 rounded-xl bg-[#2e2d29] text-[#f5f0e8] text-base font-semibold disabled:opacity-60 flex items-center justify-center gap-2"
            data-testid="hafta-kaydet">
            {kaydediyor && <Loader2 size={18} className="animate-spin" />}
            {kaydediyor ? 'Kaydediliyor…' : `Kaydet (${bekleyenSayisi} giriş)`}
          </button>
        </div>
      )}
    </main>
  );
}

/** Hücreye dokununca alttan açılan durum listesi. */
function DurumSecici({ secim, durumlar, kapat, yaz, sil, bekliyorMu }) {
  const [mesai, setMesai] = useState(secim.mevcut?.mesai_saat ? String(secim.mevcut.mesai_saat) : '');
  return (
    <div className="fixed inset-0 z-30 flex items-end bg-black/30" onClick={kapat}>
      <div className="w-full max-w-lg mx-auto rounded-t-2xl bg-[#faf7f2] p-3 max-h-[85vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()} data-testid="durum-secici">
        <div className="flex items-center justify-between mb-2">
          <div>
            <p className="font-semibold text-[#1a1916]">{secim.ad}</p>
            <p className="text-xs text-[#8a7e70]">
              {gunAy(secim.tarih)} {GUN_ADI[gunuOku(secim.tarih).getDay()]}
            </p>
          </div>
          <button type="button" onClick={kapat} className="p-2 text-[#8a7e70]" aria-label="Kapat"><X size={18} /></button>
        </div>
        <div className="grid grid-cols-2 gap-1.5">
          {durumlar.map(d => (
            <button key={d.deger} type="button" onClick={() => yaz(d.deger, mesai)}
              data-testid={`secici-${d.deger}`}
              className={`rounded-lg border px-2 py-3 text-[13px] font-medium leading-tight
                ${secim.mevcut?.durum === d.deger ? RENK[d.deger] : 'bg-white text-[#3F3E39] border-[#e0d6c6]'}`}>
              {d.etiket}
            </button>
          ))}
        </div>
        <div className="mt-3 flex items-center gap-2 text-sm text-[#3F3E39]">
          <span className="text-[#8a7e70]">Mesai (eksik için −)</span>
          <input type="number" inputMode="decimal" step="any" min="-16" max="16" value={mesai} placeholder="0"
            onChange={(e) => setMesai(e.target.value)} data-testid="secici-mesai"
            className="w-20 h-10 rounded-lg border border-[#d0c4b0] px-2 text-base text-right" />
          saat
          {bekliyorMu && (
            <button type="button" onClick={sil} className="ml-auto text-xs text-[#a34b3f] px-2 py-2"
              data-testid="secici-sil">Değişikliği geri al</button>
          )}
        </div>
      </div>
    </div>
  );
}

function Kabuk({ children }) {
  return <div className="min-h-screen bg-[#f0ebe2] max-w-lg mx-auto">{children}</div>;
}
